import { cn } from '@repo/tailwind-config/tailwind.functions'
import Input from '@repo/ui/input'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

import {
  TRANSLATION_LOCALES,
  TranslationLocale
} from '../../../lib/types/enums/translation/translation.enum'
import { Translation } from '../../../lib/types/models/translation/translation'
import { LocaleKeys } from '../../../locale/translation-types'
import { t } from '../../../locale/utils'

const LOCALE_LABEL_KEYS: Record<TranslationLocale, LocaleKeys> = {
  [TranslationLocale.FR]: 'translation.locale.FR',
  [TranslationLocale.EN]: 'translation.locale.EN',
  [TranslationLocale.DE]: 'translation.locale.DE',
  [TranslationLocale.ES]: 'translation.locale.ES'
}

type Props<T extends FieldValues> = {
  control: Control<T>
  // Form field holding a `Translation[]`
  name: Path<T>
  label?: string
  requiredLocales?: TranslationLocale[]
  locales?: TranslationLocale[]
  className?: string
}

/**
 * Wrapper over the input field that renders one input per locale for a
 * `Translation[]` form field. Locales listed in `requiredLocales` are marked
 * required.
 */
const TranslationsInput = <T extends FieldValues>({
  control,
  name,
  label,
  requiredLocales = [],
  locales = TRANSLATION_LOCALES,
  className
}: Props<T>) => {
  const { field, fieldState } = useController({ control, name })
  const translations: Translation[] = field.value ?? []

  const getValue = (locale: TranslationLocale) =>
    translations.find((tr) => tr.locale === locale)?.value ?? ''

  const setValue = (locale: TranslationLocale, value: string) => {
    const others = translations.filter((tr) => tr.locale !== locale)
    field.onChange(value ? [...others, { locale, value }] : others)
  }

  const localeLabel = (locale: TranslationLocale) => t(LOCALE_LABEL_KEYS[locale])

  // The array-level validation failed → flag each empty required locale under its own input
  const localeError = (locale: TranslationLocale) =>
    fieldState.error && requiredLocales.includes(locale) && !getValue(locale).trim()
      ? t('translation.value.required')
      : undefined

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {locales.map((locale) => (
        <Input
          key={locale}
          type="text"
          required={requiredLocales.includes(locale)}
          label={label ? `${label} (${localeLabel(locale)})` : localeLabel(locale)}
          value={getValue(locale)}
          errorMessage={localeError(locale)}
          onChange={(e) => setValue(locale, e.target.value)}
        />
      ))}
    </div>
  )
}

export default TranslationsInput
